import { useEffect, useState } from 'react';
import type { ShellUpdateState } from '@momomail/shared';
import { shell } from '../lib/bridge';
import { t, tMessage } from '../lib/i18n';
import { Icon } from './Icon';

/**
 * Desktop auto-update (main process: apps/desktop/src/updates.ts): the small "new version downloaded, restart to update" sticker on the title bar,
 * and the current version + "检查更新" (Check for updates) under Settings → General. The web version / old desktop shells have no shell.updates, so nothing is shown.
 */
export function useUpdates(): ShellUpdateState | null {
  const updates = shell?.updates;
  const [state, setState] = useState<ShellUpdateState | null>(null);
  useEffect(() => {
    if (!updates || !shell) return;
    let active = true;
    void updates
      .state()
      .then((s) => active && setState(s))
      .catch(() => {});
    const off = shell.onEvent((e) => {
      if (e.type === 'update') setState(e.state);
    });
    return () => {
      active = false;
      off();
    };
  }, [updates]);
  return state;
}

/** Title bar: appears only once downloaded; click to quit and install (reopens automatically after installing). */
export function UpdateNotice() {
  const state = useUpdates();
  if (state?.status !== 'ready' || !state.version) return null;
  return (
    <button type="button" className="titlebar-update" title={t('退出并安装新版本，装完会自动重新打开')} onClick={() => shell?.updates?.install()}>
      <Icon name="download" size={14} /> {t('新版本 {version} 已下载，重启更新', { version: state.version })}
    </button>
  );
}

/** Settings → General: current version + check for updates. */
export function UpdateSettings({ Row }: { Row: (props: { title: string; desc?: string; children: React.ReactNode }) => React.ReactElement }) {
  const state = useUpdates();
  const [asked, setAsked] = useState(false);
  if (!state) return null;
  const check = () => {
    setAsked(true);
    void shell?.updates
      ?.check()
      .then(() => {})
      .catch(() => {});
  };
  let desc: string;
  let action: React.ReactNode = (
    <button className="btn btn--small" onClick={check} disabled={state.status === 'checking'}>
      <Icon name="refresh" size={15} /> {t('检查更新')}
    </button>
  );
  switch (state.status) {
    case 'disabled':
      desc = t('开发版不自动更新。');
      action = null;
      break;
    case 'checking':
      desc = t('正在检查…');
      break;
    case 'latest':
      desc = t('已经是最新版本。');
      break;
    case 'downloading':
      desc = state.percent ? t('正在后台下载 {version}（{percent}%）', { version: state.version ?? '', percent: state.percent }) : t('正在后台下载 {version}', { version: state.version ?? '' });
      action = null;
      break;
    case 'ready':
      desc = t('新版本 {version} 已经下载好了。', { version: state.version ?? '' });
      action = (
        <button className="btn btn--small btn--primary" onClick={() => shell?.updates?.install()}>
          <Icon name="download" size={15} /> {t('重启更新')}
        </button>
      );
      break;
    case 'error':
      desc = asked ? t('检查更新失败：{error}', { error: tMessage(state.error ?? '') }) : t('上次检查更新没成功，稍后会自动再试。');
      break;
    default:
      desc = t('启动后会自动检查，每 6 小时一次；新版本在后台下载，下好了提示你重启。');
  }
  return (
    <Row title={t('当前版本 {version}', { version: state.current })} desc={desc}>
      {action}
    </Row>
  );
}
